import { useTranslation } from '@cobliteam/i18n';
import Button from '@cobliteam/ui/components/Button';
import Dropdown from '@cobliteam/ui/components/Dropdown';
import { Checkbox, Label } from '@cobliteam/ui/form';
import Icon from '@cobliteam/ui/icons';
import { useCallback, useEffect, useState } from 'react';

export interface MultiSelectIdentificationTokenStatusProps {
  isOpen?: boolean;
  size?: 'sm' | 'md';
  selectedStatuses?: string[];
  onChange: (selectedStatuses: string[]) => void;
}

const MultiSelectIdentificationTokenStatus = ({ isOpen, size = 'md', selectedStatuses = [], onChange }: MultiSelectIdentificationTokenStatusProps) => {
  const { t } = useTranslation('multiselects');
  const [forceClosed, setForceClosed] = useState(false);
  const [draft, setDraft] = useState(selectedStatuses);

  const hasSameElements = selectedStatuses.length === draft.length && selectedStatuses.every((value) => draft.includes(value));

  const options = [
    { value: 'linked', label: t('identification_token_status.linked') },
    { value: 'unlinked', label: t('identification_token_status.unlinked') },
  ];

  const onDropdownOpen = useCallback(() => {
    setDraft(selectedStatuses);
  }, [selectedStatuses]);

  const onChangeSelect = (value: string) => {
    const isSelected = draft.includes(value);
    setDraft(isSelected ? draft.filter((d) => d !== value) : [...draft, value]);
  };

  const handleClose = useCallback(() => {
    setForceClosed(true);
    setDraft(selectedStatuses);
  }, [selectedStatuses]);

  const handleApply = useCallback(() => {
    onChange(draft);
    handleClose();
  }, [draft, handleClose, onChange]);

  useEffect(() => setForceClosed(!isOpen), [isOpen]);

  useEffect(() => {
    if (forceClosed) setForceClosed(false);
  }, [forceClosed]);

  return (
    <Dropdown
      portal={false}
      onOpenChange={onDropdownOpen}
      forceClose={forceClosed}
      overlay={
        <Dropdown.Overlay>
          <Dropdown.Header className='px-2'>
            <Button variant='ghost' size='sm' disabled={draft.length === 0} onClick={() => setDraft([])}>
              {t('identification_token_status.clear_selection')}
            </Button>
          </Dropdown.Header>
          <Dropdown.Body className='px-2'>
            <Dropdown.List
              heightList={100}
              heightItem={28}
              data={options}
              itemComponent={(item) => (
                <div className='flex items-center gap-2'>
                  <Checkbox
                    id={`token-status-${item.value}`}
                    key={`token-status-${item.value}`}
                    checked={draft.includes(item.value)}
                    onCheckedChange={() => onChangeSelect(item.value)}
                    data-testid={`checkbox-identification-token-status-${item.value}`}
                  />
                  <Label htmlFor={`token-status-${item.value}`}>{item.label}</Label>
                </div>
              )}
            />
          </Dropdown.Body>
          <Dropdown.Footer>
            <Dropdown.Actions className='px-2'>
              <Button full variant='ghost' size='sm' onClick={handleClose}>
                {t('identification_token_status.cancel')}
              </Button>
              <Button full variant='contained' size='sm' color='primary' onClick={handleApply} disabled={hasSameElements}>
                {t('identification_token_status.apply')}
              </Button>
            </Dropdown.Actions>
          </Dropdown.Footer>
        </Dropdown.Overlay>
      }
    >
      <Button size={size} variant='ghost' startIcon={<Icon name='link' />}>
        {t('identification_token_status.title')} {selectedStatuses.length > 0 ? `(${selectedStatuses.length})` : ''}
      </Button>
    </Dropdown>
  );
};

export default MultiSelectIdentificationTokenStatus;
